import {
  ApiException,
  downloadUrl,
  getMetadata,
  requestCode,
  verifyCode,
  verifyPassword,
  type TransferMetadata,
} from './api';
import { escapeHtml, icon, showToast, triggerDownload } from './dom';
import { daysLeft, formatBytes, formatDateTime } from './format';

interface DownloadState {
  id: string;
  meta: TransferMetadata | null;
  session?: string;
  passwordOk: boolean;
  emailOk: boolean;
  email: string;
  codeSent: boolean;
}

export function renderDownload(root: HTMLElement, id: string): void {
  const state: DownloadState = {
    id,
    meta: null,
    passwordOk: false,
    emailOk: false,
    email: '',
    codeSent: false,
  };

  root.innerHTML = introHtml() + `<div class="panel" id="panel">${loadingHtml()}</div>`;
  const panel = root.querySelector<HTMLElement>('#panel')!;

  void load(panel, state);
}

async function load(panel: HTMLElement, state: DownloadState): Promise<void> {
  try {
    state.meta = await getMetadata(state.id);
    render(panel, state);
  } catch (err) {
    if (err instanceof ApiException && err.status === 410) {
      panel.innerHTML = expiredHtml();
    } else if (err instanceof ApiException && err.status === 404) {
      panel.innerHTML = notFoundHtml('この共有リンクは存在しないか、無効です。');
    } else {
      const message = err instanceof ApiException ? err.message : '読み込みに失敗しました';
      panel.innerHTML = notFoundHtml(message);
    }
  }
}

function render(panel: HTMLElement, state: DownloadState): void {
  const meta = state.meta;
  if (!meta) return;

  if (meta.requiresPassword && !state.passwordOk) {
    panel.innerHTML = passwordGateHtml();
    wirePasswordGate(panel, state);
    return;
  }
  if (meta.requiresEmail && !state.emailOk) {
    panel.innerHTML = emailGateHtml(state);
    wireEmailGate(panel, state);
    return;
  }
  panel.innerHTML = filesHtml(meta);
  wireFiles(panel, state);
}

function introHtml(): string {
  return `
  <div class="wrap intro">
    <h1>共有されたファイル</h1>
    <p>送信者が設定した保存期間の間だけダウンロードできます。認証が設定されている場合は、解除後にダウンロードが可能になります。</p>
  </div>`;
}

function loadingHtml(): string {
  return `<div class="screen center"><span class="spinner" role="status" aria-label="読み込み中"></span><p class="muted">読み込んでいます…</p></div>`;
}

function filesHtml(meta: TransferMetadata): string {
  const left = daysLeft(meta.expiresAt);
  const until = formatDateTime(meta.expiresAt);
  const rows = meta.files
    .map(
      (f) => `
    <div class="row">
      <div class="meta"><p class="nm">${escapeHtml(f.name)}</p><p class="mt">${formatBytes(f.size)}</p></div>
      <button class="btn btn-ink sm" type="button" data-file="${escapeHtml(f.id)}">${icon('download')}取得</button>
    </div>`,
    )
    .join('');
  const gateNote =
    meta.requiresPassword || meta.requiresEmail
      ? '認証が完了しました。'
      : 'この共有は認証不要です。';
  return `
  <div class="screen">
    <p class="eyebrow">受信</p>
    <p class="lead">${meta.files.length}件のファイルが共有されています</p>
    <p class="muted">${gateNote} 残り${left}日（<span class="mono">${escapeHtml(until)}</span>まで）で自動的に削除されます。</p>
    <div class="dl">${rows}</div>
  </div>`;
}

function wireFiles(panel: HTMLElement, state: DownloadState): void {
  panel.querySelectorAll<HTMLButtonElement>('[data-file]').forEach((btn) =>
    btn.addEventListener('click', () => {
      const fileId = btn.dataset.file!;
      triggerDownload(downloadUrl(state.id, fileId, state.session));
    }),
  );
}

function passwordGateHtml(): string {
  return `
  <div class="screen center">
    <span class="big-ic lock">${icon('lock')}</span>
    <p class="lead">パスワードで保護されています</p>
    <p class="muted">送信者から共有されたパスワードを入力してください。</p>
    <form class="gate-form" id="pw-form">
      <div class="field">
        <label for="pw">パスワード</label>
        <input class="control" id="pw" type="password" autocomplete="off" placeholder="合言葉を入力" />
        <p class="error-text" id="pw-error" hidden></p>
      </div>
      <button class="btn btn-accent block" type="submit" id="pw-submit">${icon('key')}ロックを解除</button>
    </form>
  </div>`;
}

function wirePasswordGate(panel: HTMLElement, state: DownloadState): void {
  const form = panel.querySelector<HTMLFormElement>('#pw-form')!;
  const input = panel.querySelector<HTMLInputElement>('#pw')!;
  const error = panel.querySelector<HTMLElement>('#pw-error')!;
  const submit = panel.querySelector<HTMLButtonElement>('#pw-submit')!;
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    error.hidden = true;
    submit.disabled = true;
    try {
      const res = await verifyPassword(state.id, input.value, state.session);
      state.session = res.session;
      state.passwordOk = true;
      if (res.emailVerified) state.emailOk = true;
      render(panel, state);
    } catch (err) {
      error.textContent = err instanceof ApiException ? err.message : 'パスワードの確認に失敗しました';
      error.hidden = false;
      submit.disabled = false;
    }
  });
  input.focus();
}

function emailGateHtml(state: DownloadState): string {
  return `
  <div class="screen">
    <div class="steps">
      <div class="step">
        <span class="step-n">STEP 1</span>
        <p class="lead">メールアドレスの確認</p>
        <div class="field">
          <label for="email">許可されたメールアドレス</label>
          <input class="control" id="email" type="email" autocomplete="email" placeholder="you@example.com" value="${escapeHtml(state.email)}" />
        </div>
        <button class="btn btn-ink block" type="button" id="send-code">${icon('mail')}認証コードを送信</button>
        <p class="hint">許可されていないアドレスにはコードを送信しません。</p>
      </div>
      <div class="step" id="step2" aria-disabled="${!state.codeSent}">
        <span class="step-n">STEP 2</span>
        <p class="lead">6桁の認証コードを入力</p>
        <div class="otp" role="group" aria-label="6桁の認証コード">
          ${Array.from({ length: 6 }, (_, i) => `<input class="otp-cell" inputmode="numeric" maxlength="1" aria-label="${i + 1}桁目" ${state.codeSent ? '' : 'disabled'} />`).join('')}
        </div>
        <p class="hint" id="code-msg" aria-live="polite">${state.codeSent ? `コードを ${escapeHtml(state.email)} 宛に送信しました。` : 'まず STEP 1 でコードを送信してください。'}</p>
        <p class="error-text" id="code-error" hidden></p>
        <button class="btn btn-accent block" type="button" id="verify-code" ${state.codeSent ? '' : 'disabled'}>${icon('check')}認証してダウンロード</button>
      </div>
    </div>
  </div>`;
}

function wireEmailGate(panel: HTMLElement, state: DownloadState): void {
  const emailInput = panel.querySelector<HTMLInputElement>('#email')!;
  const sendBtn = panel.querySelector<HTMLButtonElement>('#send-code')!;
  const verifyBtn = panel.querySelector<HTMLButtonElement>('#verify-code')!;
  const codeMsg = panel.querySelector<HTMLElement>('#code-msg')!;
  const codeError = panel.querySelector<HTMLElement>('#code-error')!;
  const cells = Array.from(panel.querySelectorAll<HTMLInputElement>('.otp-cell'));

  wireOtpCells(cells);

  sendBtn.addEventListener('click', async () => {
    const email = emailInput.value.trim().toLowerCase();
    if (!email) return showToast('メールアドレスを入力してください', true);
    sendBtn.disabled = true;
    try {
      const res = await requestCode(state.id, email);
      state.email = email;
      state.codeSent = true;
      codeMsg.textContent = res.message;
      cells.forEach((c) => {
        c.disabled = false;
        c.value = '';
      });
      verifyBtn.disabled = false;
      panel.querySelector('#step2')?.setAttribute('aria-disabled', 'false');
      cells[0]?.focus();
    } catch (err) {
      showToast(err instanceof ApiException ? err.message : '送信に失敗しました', true);
    } finally {
      sendBtn.disabled = false;
    }
  });

  verifyBtn.addEventListener('click', async () => {
    const code = cells.map((c) => c.value).join('');
    codeError.hidden = true;
    if (code.length !== 6) {
      codeError.textContent = '6桁のコードを入力してください';
      codeError.hidden = false;
      return;
    }
    verifyBtn.disabled = true;
    try {
      const res = await verifyCode(state.id, state.email, code, state.session);
      state.session = res.session;
      state.emailOk = true;
      if (res.passwordVerified) state.passwordOk = true;
      render(panel, state);
    } catch (err) {
      codeError.textContent = err instanceof ApiException ? err.message : '認証に失敗しました';
      codeError.hidden = false;
      verifyBtn.disabled = false;
    }
  });
}

function wireOtpCells(cells: HTMLInputElement[]): void {
  cells.forEach((cell, i) => {
    cell.addEventListener('input', () => {
      cell.value = cell.value.replace(/\D/g, '').slice(0, 1);
      if (cell.value && cells[i + 1]) cells[i + 1].focus();
    });
    cell.addEventListener('keydown', (e) => {
      if (e.key === 'Backspace' && !cell.value && cells[i - 1]) cells[i - 1].focus();
    });
    cell.addEventListener('paste', (e) => {
      e.preventDefault();
      const text = (e.clipboardData?.getData('text') ?? '').replace(/\D/g, '').slice(0, 6);
      if (!text) return;
      cells.forEach((c, j) => (c.value = text[j] ?? ''));
      cells[Math.min(text.length, cells.length - 1)]?.focus();
    });
  });
}

function expiredHtml(): string {
  return `
  <div class="screen center">
    <span class="big-ic warn">${icon('alert')}</span>
    <p class="lead">この共有リンクは期限切れです</p>
    <p class="muted">保存期間（最大7日間）を過ぎたため、ファイルは削除されました。もう一度受け取るには、送信者に再アップロードを依頼してください。</p>
    <a class="btn btn-ghost" href="/">${icon('refresh')}新しく送る</a>
  </div>`;
}

function notFoundHtml(message: string): string {
  return `
  <div class="screen center">
    <span class="big-ic warn">${icon('alert')}</span>
    <p class="lead">共有が見つかりません</p>
    <p class="muted">${escapeHtml(message)}</p>
    <a class="btn btn-ghost" href="/">${icon('refresh')}新しく送る</a>
  </div>`;
}
